import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { State } from 'src/app/app.reducer';
import { PenaltyFareBasisMachineLearning } from 'src/app/core/models/cancellation.model';
import { IOffices } from 'src/app/core/models/IGdsCredentials.model';
import { ContractError } from 'src/app/core/models/models';
import { AncillariesValidation } from 'src/app/core/models/special-requests.model';
import { DashboardService } from 'src/app/dashboard/services/dashboard.service';
import { GenericError } from 'src/app/layout/actions/layout.actions';
import { PnrActions, TabActions } from '../actions';
import * as fromReducers from '../reducers/pnr.reducer';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ENDPROCESS } from 'src/app/core/enums/enums';
import { IRequestAdditionalFlightInfo } from 'src/app/core/models/request-info-flight.model';
import {
  IPaymentWebhook,
  IPaymentForm,
  IPaymentStatus
} from 'src/app/core/models/voluntary-change.model';
import {
  IVoidTransactionQuote,
  IVoidTransactionState
} from 'src/app/core/models/void-transaction.model';
import {
  IAdditionalFlightInfo,
  ILogInLogState,
  IOAGFLTStatus,
  ISRequestValidation,
  ISRequestValidationQuote,
  Itinerary,
  Location,
  Passenger,
  PassengerMco,
  PassengerQuote,
  PnrAvailableOption,
  PnrInfo,
  QuoteRequest,
  SearchForm,
  Segment,
  WaiverData
} from 'src/app/core/models/itinerary.model';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ItineraryService {
  private env = environment;
  api = this.env.urlBase;
  pnr: string;
  gds: string;
  bookingCode: string;
  country: string;
  pnrInfo: PnrInfo;
  passengers$: Observable<Passenger[]>;
  segments$: Observable<Segment[]>;

  constructor(
    private store: Store<State>,
    private router: Router,
    private dashboard: DashboardService,
    private http: HttpClient
  ) {}

  setReloadedPageSessionStorage() {
    sessionStorage.setItem(ENDPROCESS.reloadedPage, JSON.stringify(true));
  }

  getReloadedPageSessionStorage() {
    if (sessionStorage.getItem(ENDPROCESS.reloadedPage) === 'true' && environment.production) {
      return true;
    } else {
      return false;
    }
  }

  getSearchForm(): Observable<SearchForm> {
    return this.dashboard.getSearchForm();
  }

  getQuoteRequest(): Observable<QuoteRequest> {
    return this.store.pipe(select(fromReducers.getQuoteRequest));
  }

  getQuoteRequestForInvoluntary(): Observable<QuoteRequest> {
    return this.store.pipe(select(fromReducers.selectQuoteRequestForInvoluntary));
  }

  getHasSplit(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.hasSplit));
  }

  getQuoteRequestFromState(): Observable<QuoteRequest> {
    return this.store.pipe(select(fromReducers.selectQuoteRequest));
  }

  getActions(): Observable<PnrAvailableOption> {
    return this.store.pipe(select(fromReducers.getAvailableOption));
  }

  getSelectedPassengersHasInvalidCoupons(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getSelectedPassengersHasInvalidCoupons));
  }

  getLoading(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getLoading));
  }

  getLoadingActions(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getLoadingActions));
  }

  getLoadingRules(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getLoadingRules));
  }

  getError(): Observable<ContractError> {
    return this.store.pipe(select(fromReducers.getError));
  }

  getActionActive(): Observable<string> {
    return this.store.pipe(select(fromReducers.getActionActive));
  }

  getPnrInfo(): Observable<PnrInfo> {
    return this.store.pipe(select(fromReducers.getPnrInfo));
  }

  getPassengersWithMco(): Observable<PassengerMco[]> {
    return this.store.pipe(select(fromReducers.getPassengersWithMco));
  }

  getIsExpired(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getIsExpired));
  }

  getPnrBaseInfo(): boolean {
    this.store.pipe(select(fromReducers.getPnrInfo), take(1)).subscribe((pnr) => {
      this.pnrInfo = pnr;
      this.pnr = this.pnrInfo.code;
      this.gds = this.pnrInfo.gds;
      this.country = this.pnrInfo.countryCode;
    });
    this.dashboard
      .getSearchForm()
      .pipe(take(1))
      .subscribe((form) => {
        this.bookingCode = form.bookingCode;
      });
    return !!this.pnrInfo ? true : false;
  }

  getItinerary(): Observable<Itinerary> {
    return this.store.pipe(select(fromReducers.getItinerary));
  }

  getSelectedPassengersQuote(): Observable<PassengerQuote[]> {
    return this.store.pipe(select(fromReducers.getSelectedPassengersQuote));
  }

  getSelectedSegments(): Observable<number[]> {
    return this.store.pipe(select(fromReducers.getSelectedSegments));
  }

  getPenaltiesFareBasis(): Observable<PenaltyFareBasisMachineLearning> {
    return this.store.pipe(select(fromReducers.getPenaltiesFareBasis));
  }

  getRulesAI(): Observable<PenaltyFareBasisMachineLearning | ContractError> {
    return this.store.pipe(select(fromReducers.getRulesFarebasis));
  }

  dispatcPnrInfoSuccess(pnrInfo: PnrInfo) {
    this.store.dispatch(PnrActions.LoadPnrSuccess({ pnrInfo }));
  }

  dispatchSetSelectedPassengersHasInvalidCoupons(selectedPassengersHasInvalidCoupons: boolean) {
    this.store.dispatch(
      PnrActions.SetSelectedPassengersHasInvalidCoupons({ selectedPassengersHasInvalidCoupons })
    );
  }

  dispatchClearPnrInfo() {
    this.store.dispatch(PnrActions.ClearPnrInfo());
  }

  dispatchClearPnrPrevSuccess() {
    this.store.dispatch(PnrActions.ClearPnrSuccess());
  }

  dispatchLoadPnr() {
    this.store.dispatch(PnrActions.LoadPnr());
  }

  dispatchSelectedSegment(selectedSegments: number[]) {
    this.store.dispatch(PnrActions.LoadSelectedSegments({ selectedSegments }));
  }

  dispatchSelectedPassengers(selectedPassengersQuote: PassengerQuote[]) {
    this.store.dispatch(PnrActions.LoadSelectedPassengers({ selectedPassengersQuote }));
  }

  dispatchGenericError(error: ContractError) {
    this.store.dispatch(GenericError({ error }));
  }

  dispatchOptionQuote(quoteRequest: QuoteRequest) {
    this.getPnrBaseInfo();
    quoteRequest.pnr = this.pnr;
    quoteRequest.gds = this.gds;
    quoteRequest.transactionId = this.pnrInfo.transactionId;
    quoteRequest.country = this.country;
    quoteRequest.bookingCode = this.bookingCode;
    quoteRequest.officeId = this.pnrInfo.officeId;
    quoteRequest.validatingCarrier = this.pnrInfo.validatingCarrier;
    quoteRequest.selectedPassengers = quoteRequest.passengers;
    quoteRequest.passengers = this.pnrInfo.passengers as unknown as PassengerQuote[];
    quoteRequest.itinerary = this.pnrInfo.itinerary;
    quoteRequest.isOpen = this.pnrInfo.isOpen;
    quoteRequest.isPurgued = this.pnrInfo.isPurgued;
    quoteRequest.flights = quoteRequest?.flights?.length
      ? quoteRequest?.flights
      : this.getAllFlights(this.pnrInfo?.itinerary?.segments);
    this.store.dispatch(PnrActions.LoadAvailableOption({ quoteRequest }));
  }

  getAllFlights(segments: Segment[]): number[] {
    const flights = [];
    segments.map((res) => {
      res?.flights.map((res) => {
        flights.push(res?.index);
      });
    });
    return flights;
  }

  dispatchCloseModalAction() {
    this.store.dispatch(PnrActions.CloseModalAction());
  }

  dispatchChooseActionMode(actionActive: string) {
    this.store.dispatch(PnrActions.ChooseActionMode({ actionActive }));
  }

  navigateToWithParams(route: string) {
    combineLatest([this.dashboard.getSearchForm(), this.getPnrInfo()])
      .pipe(take(1))
      .subscribe(([form, pnr]) => this.navigateTo(route, form));
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  private nav(route: string, bookingCode: string, isTKT: boolean, value: string, gds: string) {
    this.router.navigate([route], {
      queryParams: {
        bookingCode,
        isTKT,
        value,
        gds
      }
    });
  }

  navigateTo(route: string, params?: any) {
    this.router.navigate([route], { queryParams: params });
  }

  getActiveTab(): Observable<string> {
    return this.store.pipe(select(fromReducers.getActiveTab));
  }

  getExceptionMode(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getExceptionMode));
  }

  getShowExceptionSwitch(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getShowExceptionSwitch));
  }

  getSomePassengerHasTicket(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.somePassengerHasTicket));
  }

  getSomePassengerHasAncillaryTypeS(): Observable<Passenger[]> {
    return this.store.pipe(select(fromReducers.getSomePassengerHasAncillaryTypeS));
  }

  dispatchRouteTabTo(activeTab: string) {
    this.store.dispatch(TabActions.RouteTabTo({ activeTab }));
  }

  dispatchHideOrShowExceptionSwitch(show: boolean) {
    this.store.dispatch(TabActions.HideOrShowExceptionSwitch({ show }));
  }
  dispatchExceptionMode(exceptionMode: boolean) {
    this.store.dispatch(TabActions.ChangeExceptionMode({ exceptionMode }));
  }

  dispatchSaveWaiverData(waiverData: WaiverData) {
    this.store.dispatch(TabActions.SaveWaiverData({ waiverData }));
  }

  getOverrideSegments(): Observable<number[]> {
    return this.store.pipe(select(fromReducers.getOverrideSegments));
  }

  setOverrideSegments(overrideSegments: number[]) {
    this.store.dispatch(PnrActions.UpdateOverrideSegments({ overrideSegments }));
  }

  addLabelToLocation(location: Location): Location {
    return {
      ...location,
      label: `${location.code ? location.code : ''}  ${
        location.name ? ' - ' + location.name : ''
      }  ${location.airportName ? ' - ' + location.airportName : ''}`
    };
  }

  setCommission(value: number) {
    this.store.dispatch(PnrActions.setCommision({ value }));
  }

  dispatchUpdatePnrInfo(pnrInfo: PnrInfo) {
    this.store.dispatch(PnrActions.UpdatePnrInfo({ pnrInfo }));
  }

  dispatchUpdateAncillariesValidation(ancillaries: AncillariesValidation[]) {
    this.store.dispatch(PnrActions.UpdateAncillariesValidation({ ancillaries }));
  }

  dispatchVoidTransaction(quoteRequest: IVoidTransactionQuote) {
    this.store.dispatch(PnrActions.ForceRefreshSesionAndVoid({ quoteRequest }));
  }

  getVoidTransactionState(): Observable<IVoidTransactionState> {
    return this.store.pipe(select(fromReducers.selectVoidTransactionState));
  }

  dispatchLoadPenaltyTextAI(quoteRequest: QuoteRequest) {
    this.store.dispatch(PnrActions.LoadPenaltyTextAI({ quoteRequest }));
  }

  dispatchLoadRulesAI(quoteRequest: QuoteRequest) {
    this.store.dispatch(PnrActions.LoadRulesIA({ quoteRequest }));
  }

  dispatchSRequestValidationPerFlight(quoteRequest: ISRequestValidationQuote) {
    this.store.dispatch(PnrActions.SRequestValidationPerFlight({ quoteRequest }));
  }

  dispatchResetSRequestValidationPerFlight() {
    this.store.dispatch(PnrActions.ResetSRequestValidationPerFlight());
  }

  getSRequestValidationResponse(): Observable<ISRequestValidation[]> {
    return this.store.pipe(select(fromReducers.getSRequestValidationResponse));
  }

  getSRequestValidationError(): Observable<boolean> {
    return this.store.pipe(select(fromReducers.getSRequestValidationError));
  }

  dispatchSetOfficeCountry(officeCountry: IOffices) {
    this.store.dispatch(PnrActions.LoadOfficeCountry({ officeCountry }));
  }

  dispatchGetAdditionalFlightInfo(requestAdditionalFlightInfo: IRequestAdditionalFlightInfo) {
    this.store.dispatch(PnrActions.GetAdditionalFlightInfo({ requestAdditionalFlightInfo }));
  }

  getAdditionalFlightInfo(): Observable<IAdditionalFlightInfo> {
    return this.store.pipe(select(fromReducers.selectAdditionalFlightInfo));
  }

  getVoluntaryOverrideSegments(): Observable<number[]> {
    return this.store.pipe(select(fromReducers.selectVoluntaryOverrideSegments));
  }

  getaPymentwebhook(infoPayment: IPaymentWebhook) {
    const urlRequest = `${this.api}${this.env.paymentWebhook}`;
    return this.http.post<IPaymentForm>(urlRequest, infoPayment, httpOptions);
  }

  getaStatusPayment(transactionId: string): Observable<IPaymentStatus> {
    const urlRequest = `${this.api}${this.env.paymentStatus}`;
    return this.http.get<IPaymentStatus>(`${urlRequest}/${transactionId}`, httpOptions);
  }

  dispatchSetReceivedFrom(receivedFrom: string): void {
    this.store.dispatch(PnrActions.SetReceivedFrom({ receivedFrom }));
  }

  dispatchRemoveReceivedFrom(): void {
    this.store.dispatch(PnrActions.RemoveReceivedFrom());
  }

  dispatchUpdatePassengersQuote(selectedPassengersQuote: PassengerQuote[]): void {
    this.store.dispatch(PnrActions.UpdatePassengersQuote({ selectedPassengersQuote }));
  }

  dispatchSetVoluntaryOverrideSegments(voluntaryOverrideSegments: number[]): void {
    this.store.dispatch(PnrActions.SetVoluntaryOverrideSegments({ voluntaryOverrideSegments }));
  }

  dispatchGetOAGFLTStatus() {
    this.store.dispatch(PnrActions.GetOAGFLTStatus());
  }

  getIOAGFLTStatus(): Observable<IOAGFLTStatus> {
    return this.store.pipe(select(fromReducers.selectOAGFLTStatus));
  }

  dispatchPostLogInLog(): void {
    this.store.dispatch(PnrActions.RegisterUserLogInLog());
  }

  getLogInLogState(): Observable<ILogInLogState> {
    return this.store.pipe(select(fromReducers.selectLogInLogState));
  }
}
